import { useEffect, useState } from "react";
import { Button, Form, Modal, Table, Badge, Card, Row, Col } from "react-bootstrap";
import Pagination from 'react-js-pagination';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faHome, faCheckCircle, faTimesCircle, faUser, faCalendarAlt, faTrash, faBuilding, faMapMarkerAlt, faInfoCircle, faPhone } from '@fortawesome/free-solid-svg-icons';

import ViewItemsPerPage from "../ViewItemsPerPage";
import { Link } from "react-router-dom";
import { Enumeration, OptionType, ListProperty, SearchProperty, StatusItem } from "../../utils/interface";
import SearchableDropdown from "../SearchableDropdown";
import { formatEnumsForDropDown, formatStatuesForDropDown } from "../../utils/helperFunction";

interface ListPropertiesProps {
  propertiesList: ListProperty[];
  propertiesListCount: number;
  propertiesListPageIndex: number;
  propertiesListPageSize: number;
  onChangePageIndex: (pageNumber: number) => void;
  onChangePageSize: (pageSize: number) => void;
  findOptions: SearchProperty;
  handleFindProperty: (findOptions: SearchProperty) => void;
  resetFindOptions: () => void;
  propertyStatuses: StatusItem[];
  propertyTypes: Enumeration[];
  fetchOwners: (searchTerm: string, pageIndex: number, pageSize: number) =>
    Promise<{ options: { value: number; label: string; }[], hasMore: boolean }>;
  onDeleteProperty: (propertyId: number) => void;
}

const ListProperties: React.FC<ListPropertiesProps> = ({
  propertiesList,
  propertiesListCount,
  propertiesListPageIndex,
  propertiesListPageSize,
  onChangePageIndex,
  onChangePageSize,
  findOptions,
  handleFindProperty,
  resetFindOptions,
  fetchOwners,
  propertyStatuses,
  propertyTypes,
  onDeleteProperty,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [searchOptions, setSearchOptions] = useState(findOptions);
  const [selectedOwner, setSelectedOwner] = useState<OptionType | null>(null);

  const handleModalClose = () => setShowModal(false);
  const handleModalOpen = () => setShowModal(true);

  const onChangeFieldValue = (value: object) => {
    setSearchOptions({ ...searchOptions, ...value });
  }
  const onClickFind = (findOptions: SearchProperty) => {
    handleFindProperty(findOptions);
    handleModalClose();
  };
  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeFieldValue({ isVerified: event.target.value === 'yes' });
  };

  useEffect(() => { setSearchOptions(findOptions) }, [findOptions])

  return (
    <div className="property-list-container">
      <style>{`
        .property-list-container {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .modern-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          background: #fff;
          overflow: hidden;
        }
        .modern-table {
          margin-bottom: 0;
        }
        .modern-table thead th {
          background-color: #f8f9fa;
          color: #495057;
          font-weight: 700;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e9ecef;
          padding: 1rem;
        }
        .modern-table tbody td {
          padding: 1rem;
          vertical-align: middle;
          border-bottom: 1px solid #f1f3f5;
        }
        .property-icon {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          margin-right: 15px;
          box-shadow: 0 4px 10px rgba(49, 130, 206, 0.3);
          flex-shrink: 0;
        }
        .icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }
        .icon-btn:hover {
          transform: translateY(-2px);
        }
        /* Mobile Card Inspiration Styles */
        .mobile-inspiration-card {
          background: #fff;
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.06);
        }
        .mobile-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        .mobile-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.4rem;
        }
        .mobile-card-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
        }
        .mobile-card-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 4px;
        }
        .mobile-card-subtitle {
          font-size: 0.9rem;
          color: #4a5568;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .mobile-card-desc {
          font-size: 0.85rem;
          color: #718096;
          line-height: 1.4;
          margin-bottom: 15px;
        }
        .mobile-card-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .mobile-pill {
          background: #f7fafc;
          border: 1px solid #edf2f7;
          border-radius: 20px;
          padding: 6px 14px;
          font-size: 0.75rem;
          color: #4a5568;
          font-weight: 600;
        }
        @media (min-width: 769px) {
          .property-icon-wrapper {
            display: flex;
            align-items: center;
          }
        }
      `}</style>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: '#2d3748' }}>Properties Directory</h4>
          <p className="text-muted mb-0 small">Manage and view all listed properties.</p>
        </div>
        <Button variant="primary" className="shadow-sm rounded-pill px-4" onClick={handleModalOpen} style={{ background: 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)', border: 'none' }}>
          <FontAwesomeIcon icon={faSearch} className="me-2" /> Find Options
        </Button>
      </div>

      <Modal show={showModal} onHide={handleModalClose} centered size="lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">Find Property</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-property-id">
                  <Form.Label className="small fw-semibold text-muted">PROPERTY ID</Form.Label>
                  <Form.Control
                    type="text"
                    className="rounded-3"
                    value={searchOptions.propertyId ? searchOptions.propertyId : ''}
                    onChange={(e) => onChangeFieldValue({ propertyId: parseInt(e.target.value) })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-property-title">
                  <Form.Label className="small fw-semibold text-muted">PROPERTY TITLE</Form.Label>
                  <Form.Control
                    type="text"
                    className="rounded-3"
                    value={searchOptions.propertyTitle ? searchOptions.propertyTitle : ''}
                    onChange={(e) => onChangeFieldValue({ propertyTitle: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-property-type">
                  <Form.Label className="small fw-semibold text-muted">PROPERTY TYPE</Form.Label>
                  <Select
                    options={formatEnumsForDropDown(propertyTypes)}
                    isSearchable
                    placeholder=""
                    value={
                      formatEnumsForDropDown(propertyTypes)
                        .find(option => option.value === findOptions.propertyTypeEnumId)
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        onChangeFieldValue({ propertyTypeEnumId: selectedOption?.value })
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="find-status">
                  <Form.Label className="small fw-semibold text-muted">STATUS</Form.Label>
                  <Select
                    options={formatStatuesForDropDown(propertyStatuses)}
                    isSearchable
                    placeholder=""
                    value={
                      formatStatuesForDropDown(propertyStatuses)
                        .find(option => option.value === findOptions.statusId)
                    }
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        onChangeFieldValue({ statusId: selectedOption.value })
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="find-owner">
              <Form.Label className="small fw-semibold text-muted">OWNER</Form.Label>
              <SearchableDropdown
                placeholder="Search owner..."
                fetchData={fetchOwners}
                onChange={(selectedValue: any) => {
                  onChangeFieldValue({ ownerId: selectedValue.value });
                  setSelectedOwner(selectedValue);
                }}
                selectedData={selectedOwner}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="is-verified-property">
              <Form.Label className="small fw-semibold text-muted me-3">IS VERIFIED?</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Yes"
                  value="yes"
                  checked={searchOptions.isVerified !== undefined ? searchOptions.isVerified === true : false}
                  onChange={handleRadioChange}
                  name="isVerified"
                  id="isVerifiedYes"
                />
                <Form.Check
                  inline
                  type="radio"
                  label="No"
                  value="no"
                  checked={searchOptions.isVerified !== undefined ? searchOptions.isVerified === false : false}
                  onChange={handleRadioChange}
                  name="isVerified"
                  id="isVerifiedNo"
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" className="rounded-pill px-4" onClick={() => {
            resetFindOptions();
            setSelectedOwner(null);
          }}>Clear</Button>
          <Button variant="primary" className="rounded-pill px-4 shadow-sm" onClick={() => onClickFind(searchOptions)}>Apply Search</Button>
        </Modal.Footer>
      </Modal>

      <div className="d-none d-md-block">
        <Card className="modern-card mb-4">
          <div className="table-responsive">
            <Table className="modern-table" hover>
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Prop #</th>
                  <th>Title & Type</th>
                  <th>Verified</th>
                  <th>Owner Details</th>
                  <th>Status & Date</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {propertiesList.map((property: ListProperty) => (
                  <tr key={property.propertyId}>
                    <td className="fw-bold text-primary" style={{ fontSize: '1.05rem' }}>
                      <Link to={`${property.propertyId}`} className="text-decoration-none">#{property.propertyId}</Link>
                    </td>
                    <td>
                      <div className="property-icon-wrapper">
                        <div className="property-icon">
                          <FontAwesomeIcon icon={faBuilding} />
                        </div>
                        <div className="text-start">
                          <div className="fw-bold" style={{ color: '#2d3748', fontSize: '1.05rem' }}>
                            <Link to={`${property.propertyId}`} className="text-decoration-none text-dark">{property.propertyTitle}</Link>
                          </div>
                          <Badge bg="info" className="rounded-pill mt-2 px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}>{property.propertyTypeEnumDescription}</Badge>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="text-start mt-2">
                        {property.isVerified ? 
                          <Badge bg="success" className="rounded-pill px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Verified</Badge> : 
                          <Badge bg="danger" className="rounded-pill px-3 py-2 fw-bold text-white" style={{ fontSize: '0.9rem' }}><FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Unverified</Badge>
                        }
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column text-start mt-2">
                        <div className="text-dark fw-bold mb-1" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faUser} className="me-2 text-primary" style={{ width: '18px' }} />
                          {property.ownerFullName}
                        </div>
                        <div className="text-dark fw-bold" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faPhone} className="me-2 text-success" style={{ width: '18px' }} />
                          {property.ownerMobile || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column text-start mt-2">
                        <div className="text-dark fw-bold mb-1 text-capitalize" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" style={{ width: '18px' }} />
                          {property.statusDescription}
                        </div>
                        <div className="text-dark fw-bold" style={{ fontSize: '1rem' }}>
                          <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-secondary" style={{ width: '18px' }} />
                          {property.createdAt.substring(0, 10)}
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-2 mt-2">
                        <Button
                          variant="outline-danger"
                          className="icon-btn border-0 shadow-sm"
                          title="Delete Property"
                          onClick={() => onDeleteProperty(property.propertyId)}
                        >
                          <FontAwesomeIcon icon={faTrash as any} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {propertiesList.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-muted">
                      No properties found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Mobile Custom Card View */}
      <div className="d-block d-md-none pb-4">
        {propertiesList.map((property: ListProperty) => (
          <div key={`mob-${property.propertyId}`} className="mobile-inspiration-card">
            <div className="mobile-card-header">
              <div className="mobile-card-icon bg-light text-primary">
                <FontAwesomeIcon icon={faBuilding} />
              </div>
              
              {property.isVerified ? 
                <Badge bg="success" className="mobile-card-badge shadow-sm"><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Verified</Badge> : 
                <Badge bg="danger" className="mobile-card-badge shadow-sm"><FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Unverified</Badge>
              }

              <Button
                variant="light"
                size="sm"
                className="border-0 shadow-none bg-transparent p-0 text-danger"
                title="Delete Property"
                onClick={() => onDeleteProperty(property.propertyId)}
              >
                <i className="fas fa-trash fa-lg"></i>
              </Button>
            </div>
            
            <div className="mobile-card-title">
              <Link to={`${property.propertyId}`} className="text-decoration-none text-dark">{property.propertyTitle}</Link>
            </div>
            <div className="mobile-card-subtitle">@{property.ownerFullName || 'N/A'}</div>
            <div className="mobile-card-desc">
              Owner Contact: {property.ownerMobile || 'N/A'} <br/>
              Added on: {property.createdAt.substring(0, 10)}
            </div>
            
            <div className="mobile-card-pills">
              <span className="mobile-pill">
                {property.propertyTypeEnumDescription}
              </span>
              <span className="mobile-pill">
                {property.statusDescription}
              </span>
            </div>
          </div>
        ))}
        {propertiesList.length === 0 && (
          <div className="text-center py-5 text-muted">
            No properties found matching your criteria.
          </div>
        )}
      </div>
      
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center bg-white p-3 rounded-3 shadow-sm mb-4">
        <ViewItemsPerPage
          selectedPageSize={propertiesListPageSize}
          onSelectPageSize={onChangePageSize}
        />
        <div className="mt-3 mt-md-0">
          <Pagination
            activePage={propertiesListPageIndex + 1}
            totalItemsCount={propertiesListCount}
            itemsCountPerPage={propertiesListPageSize}
            onChange={onChangePageIndex}
            itemClass="page-item"
            linkClass='page-link shadow-none'
            firstPageText="First"
            lastPageText="Last"
          />
        </div>
      </div>
    </div>
  );
};

export default ListProperties;
